// Anfahrtskosten außerhalb des Betriebsgebiets — Geometrie, Gating und Rundung.
//
// Gegenstücke (Änderungen synchron halten!):
//   - App:  lib/services/service_area_service.dart
//   - CF:   functions/src/index.ts (pickupServiceAreaStatus,
//           nearestServiceAreaBoundaryPoint, calcAnfahrt)
// Konzept: docs/general/anfahrtspreis_konzept.md (App-Repo).
//
// Der Routen-Call (Grenzpunkt → Abholadresse, OHNE Verkehr/departure_time —
// sonst teurerer SKU) passiert im Aufrufer (RideForm); dieses Modul liefert
// Status, Grenzpunkt, Luftlinien-Fallback und die Betrag/Notiz-Ableitung.

import { SERVICE_AREA_FALLBACK_RADIUS_METERS } from './serviceAreaBias';

const EARTH_METERS_PER_DEGREE_LAT = 111320;

const isFiniteNumber = (value) =>
  typeof value === 'number' && Number.isFinite(value);

// Koordinaten kommen aus dem Portal teils als String (wie getCoordinate in den CF).
const toCoord = (value) => {
  if (isFiniteNumber(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const safeCosLat = (latitude) => {
  const c = Math.cos((latitude * Math.PI) / 180);
  return Math.abs(c) < 1e-6 ? 1e-6 : c;
};

// Alle aktiven Polygone mit >= 3 gültigen Punkten (Punkte unter `points` oder
// `polygon`). Achtung: getActivePolygonPoints in serviceAreaBias.js nimmt nur
// das ERSTE aktive Polygon (Bias-Zweck) — hier zählen alle, wie in den CF.
const activePolygons = (globalSettings) => {
  const raw = globalSettings?.serviceAreaPolygons;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((polygon) => polygon && polygon.active !== false)
    .map((polygon) => {
      const points = (Array.isArray(polygon.points) ? polygon.points : polygon.polygon) || [];
      return {
        bounds: polygon.bounds || null,
        points: (Array.isArray(points) ? points : []).filter(
          (p) => p && isFiniteNumber(toCoord(p.lat)) && isFiniteNumber(toCoord(p.lng))
        ).map((p) => ({ lat: toCoord(p.lat), lng: toCoord(p.lng) })),
      };
    })
    .filter((polygon) => polygon.points.length >= 3);
};

const pointInBounds = (point, bounds) => {
  if (!bounds) return true;
  return (
    point.lat <= bounds.north &&
    point.lat >= bounds.south &&
    point.lng <= bounds.east &&
    point.lng >= bounds.west
  );
};

// Ray-Casting, numerisch identisch mit pointInPolygon in den CF.
const pointInPolygon = (point, polygon) => {
  if (polygon.length < 3) return false;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lng;
    const yi = polygon[i].lat;
    const xj = polygon[j].lng;
    const yj = polygon[j].lat;
    const intersects =
      (yi > point.lat) !== (yj > point.lat) &&
      point.lng < ((xj - xi) * (point.lat - yi)) / (yj - yi || 1e-12) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
};

// Haversine, gleiche Formel wie distanceMeters in den CF.
export const haversineMeters = (a, b) => {
  const earthRadius = 6371000;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return earthRadius * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

/**
 * inside/outside/unknown für einen Punkt — gleiche Semantik wie
 * pickupServiceAreaStatus() in den Cloud Functions.
 *
 * @param {object} globalSettings state.settings.global
 * @param {{lat: number, lng: number}} point
 */
export const serviceAreaStatus = (globalSettings, point) => {
  if (!globalSettings || globalSettings.serviceAreaEnabled !== true) return 'unknown';
  const mode = globalSettings.serviceAreaMode;
  if (mode === 'disabled') return 'unknown';
  // Unbekannte Modi fallen wie in der CF-Normalisierung auf 'radius' zurück.

  if (mode === 'polygon') {
    const polygons = activePolygons(globalSettings);
    if (polygons.length > 0) {
      const inside = polygons.some(
        (polygon) => pointInBounds(point, polygon.bounds) && pointInPolygon(point, polygon.points)
      );
      return inside ? 'inside' : 'outside';
    }
  }

  const lat = toCoord(globalSettings.serviceAreaFallbackLat);
  const lng = toCoord(globalSettings.serviceAreaFallbackLng);
  if (lat !== null && lng !== null) {
    const d = haversineMeters({ lat, lng }, point);
    return d <= SERVICE_AREA_FALLBACK_RADIUS_METERS ? 'inside' : 'outside';
  }
  return 'unknown';
};

// Projektion des Punkts auf die Kante a→b in lokalen Metern (äquirektangulär
// um den Punkt; für Distanzen bis ~50 km ausreichend).
const nearestPointOnSegment = (p, a, b) => {
  const cosLat = safeCosLat(p.lat);
  const ax = (a.lng - p.lng) * EARTH_METERS_PER_DEGREE_LAT * cosLat;
  const ay = (a.lat - p.lat) * EARTH_METERS_PER_DEGREE_LAT;
  const bx = (b.lng - p.lng) * EARTH_METERS_PER_DEGREE_LAT * cosLat;
  const by = (b.lat - p.lat) * EARTH_METERS_PER_DEGREE_LAT;
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  let t = lenSq < 1e-9 ? 0 : -(ax * dx + ay * dy) / lenSq;
  t = Math.min(1, Math.max(0, t));
  const nx = ax + t * dx;
  const ny = ay + t * dy;
  return {
    lat: p.lat + ny / EARTH_METERS_PER_DEGREE_LAT,
    lng: p.lng + nx / (EARTH_METERS_PER_DEGREE_LAT * cosLat),
  };
};

/**
 * Nächstgelegener Punkt der Betriebsgebietsgrenze (Luftlinien-Projektion).
 * Startpunkt des Anfahrts-Routen-Calls; Google snappt auf die nächste Straße.
 *
 * @returns {{lat: number, lng: number} | null}
 */
export const nearestBoundaryPoint = (globalSettings, point) => {
  if (!globalSettings || globalSettings.serviceAreaEnabled !== true) return null;
  const mode = globalSettings.serviceAreaMode;
  if (mode === 'disabled') return null;

  if (mode === 'polygon') {
    const polygons = activePolygons(globalSettings);
    if (polygons.length > 0) {
      let best = null;
      let bestDist = Infinity;
      polygons.forEach((polygon) => {
        const pts = polygon.points;
        for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
          const candidate = nearestPointOnSegment(point, pts[j], pts[i]);
          const d = haversineMeters(point, candidate);
          if (d < bestDist) {
            bestDist = d;
            best = candidate;
          }
        }
      });
      return best;
    }
  }

  const lat = toCoord(globalSettings.serviceAreaFallbackLat);
  const lng = toCoord(globalSettings.serviceAreaFallbackLng);
  if (lat !== null && lng !== null) {
    // Radius-Modus: Schnittpunkt der Strecke Zentrum→Punkt mit dem Kreis.
    const cosLat = safeCosLat(lat);
    const x = (point.lng - lng) * EARTH_METERS_PER_DEGREE_LAT * cosLat;
    const y = (point.lat - lat) * EARTH_METERS_PER_DEGREE_LAT;
    const len = Math.hypot(x, y);
    if (len < 1e-9) return null;
    const s = SERVICE_AREA_FALLBACK_RADIUS_METERS / len;
    return {
      lat: lat + (y * s) / EARTH_METERS_PER_DEGREE_LAT,
      lng: lng + (x * s) / (EARTH_METERS_PER_DEGREE_LAT * cosLat),
    };
  }
  return null;
};

/**
 * Anfahrtspreis pro km mit Cap auf den Tarif-km-Preis (defensiv gegen Alt-Daten;
 * das Portal validiert die Eingabe zusätzlich).
 */
export const effectiveAnfahrtPreisProKm = (globalSettings) => {
  const preis = toCoord(globalSettings?.anfahrtPreisProKm) ?? 0.5;
  const tarif = toCoord(globalSettings?.tarifPreisProKm) ?? 2.5;
  return Math.min(preis, tarif);
};

/**
 * Fällt für diese Fahrt eine Anfahrtsgebühr an? (Konzept Abschnitt 2 — die
 * Festpreis-/Krankenfahrt-Ausnahmen prüft der Aufrufer.)
 *
 * @param {{lat, lng}} pickup
 * @param {{lat, lng} | null} destination
 */
export const shouldChargeAnfahrt = (globalSettings, pickup, destination) => {
  if (!globalSettings || globalSettings.anfahrtEnabled !== true) return false;
  if (!pickup || !destination) return false;
  return (
    serviceAreaStatus(globalSettings, pickup) === 'outside' &&
    serviceAreaStatus(globalSettings, destination) === 'outside'
  );
};

/**
 * km (auf 0,1 aufgerundet) und Betrag (auf volle 10 Cent gerundet) aus einer
 * Strecke in Metern (Routen-km, bei API-Fehler Luftlinie).
 *
 * @returns {{km: number, eur: number} | null}
 */
export const calcAnfahrtFromMeters = (distanceMeters, preisProKm) => {
  if (!isFiniteNumber(distanceMeters) || distanceMeters <= 0) return null;
  const km = Math.ceil((distanceMeters / 1000) * 10) / 10;
  if (km <= 0) return null;
  const eur = Math.round(km * preisProKm * 10) / 10;
  if (eur <= 0) return null;
  return { km, eur };
};

/** Auto-Notiz für Fahrer/Disponenten (an rides.notes anhängen). */
export const buildAnfahrtNote = ({ km, eur }) =>
  `Anfahrtskosten ${eur.toFixed(2).replace('.', ',')} € zusätzlich kassieren ` +
  `(Abholung ${km.toFixed(1).replace('.', ',')} km außerhalb Betriebsgebiet).`;

// Entfernt eine zuvor angehängte Anfahrts-Notiz (samt „ · "-Trenner) aus einem
// Notiztext — nötig beim erneuten Bearbeiten, damit sie nicht doppelt entsteht.
const ANFAHRT_NOTE_REGEX =
  /\s*·?\s*Anfahrtskosten [\d,]+ € zusätzlich kassieren \(Abholung [\d,]+ km außerhalb Betriebsgebiet\)\.?/g;

export const stripAnfahrtNote = (notes) => {
  if (!notes || typeof notes !== 'string') return notes ?? null;
  const cleaned = notes.replace(ANFAHRT_NOTE_REGEX, '').trim();
  return cleaned.length > 0 ? cleaned : null;
};
